import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABELS } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { FileText, CalendarClock, BellRing, CheckCircle2, TrendingUp, Wrench, Sun, Flame, CalendarDays, ArrowRight, User as UserIcon, MessageSquare, ClipboardList, Users as UsersIcon } from "lucide-react";
import { collectProjectTermine, buildCategoryMap, makeDayContent, CategoryLegend, terminCategory, CATEGORY_META } from "@/pages/Projekttermine";

const kpiConfig = [
  { key: "total_anmeldungen", label: "Anmeldungen gesamt", icon: FileText, tint: "text-primary", to: "/anmeldungen" },
  { key: "total_projects", label: "Projekte aktiv", icon: Wrench, tint: "text-emerald-600 dark:text-emerald-400", to: "/projekte" },
  { key: "termine_upcoming", label: "Anstehende Zählertermine", icon: CalendarClock, tint: "text-sky-600 dark:text-sky-400", to: "/zaehlertermine" },
  { key: "reminders_open", label: "Offene Erinnerungen", icon: BellRing, tint: "text-amber-600 dark:text-amber-400", to: "/erinnerungen" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [projects, setProjects] = useState([]);
  const [monteure, setMonteure] = useState([]);
  const [calDate, setCalDate] = useState(new Date());
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [recentMsgs, setRecentMsgs] = useState([]);
  const [workload, setWorkload] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, r, p, m, u, partners, wl] = await Promise.all([
        api.get("/stats").then((x) => x.data),
        api.get("/anmeldungen").then((x) => x.data),
        api.get("/projects").then((x) => x.data).catch(() => []),
        api.get("/monteure").then((x) => x.data).catch(() => []),
        api.get("/messages/unread-count").then((x) => x.data).catch(() => ({ unread: 0 })),
        api.get("/messages/users").then((x) => x.data).catch(() => []),
        api.get("/sachbearbeiter/workload").then((x) => x.data).catch(() => null),
      ]);
      setStats(s); setRecent(r.slice(0, 5)); setProjects(p); setMonteure(m);
      setUnreadMsg(u?.unread || 0);
      setRecentMsgs((partners || []).filter((x) => x.unread > 0 || x.last_at).slice(0, 5));
      setWorkload(wl);
    })().catch(() => {});
  }, []);

  const monteurName = (id) => monteure.find((mn) => mn.id === id)?.name || monteure.find((mn) => mn.id === id)?.email;

  const projTermine = useMemo(() => collectProjectTermine(projects), [projects]);
  const categoryMap = useMemo(() => buildCategoryMap(projTermine), [projTermine]);
  const DayContent = useMemo(() => makeDayContent(categoryMap), [categoryMap]);
  const fmtLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const upcoming = useMemo(() => {
    const todayStr = fmtLocal(new Date());
    return projTermine.filter((t) => t.termin >= todayStr).slice(0, 5);
  }, [projTermine]);
  const calDateStr = fmtLocal(calDate);
  const calDayItems = projTermine.filter((t) => t.termin === calDateStr);

  return (
    <div className="p-4 lg:p-8 space-y-8 animate-fade-up" data-testid="dashboard-page">
      <div>
        <div className="overline">Übersicht</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Aktueller Stand aller Aufträge.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiConfig.map(({ key, label, icon: Icon, tint, to }) => (
          <Link to={to} key={key} data-testid={`kpi-${key}`}>
            <Card className="border-border hover:border-primary/60 hover:shadow-sm transition-all cursor-pointer h-full">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
                    <div className="font-heading text-3xl lg:text-4xl font-bold mt-2 tracking-tight">{stats?.[key] ?? "—"}</div>
                  </div>
                  <Icon className={`h-6 w-6 ${tint}`} />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Link to="/projekte?type=pv" data-testid="kpi-pv">
          <Card className="border-border hover:border-primary/60 transition-colors cursor-pointer">
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center"><Sun className="h-5 w-5 text-primary" /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">PV-Installationen (DC + AC)</div>
                  <div className="font-heading text-2xl font-bold mt-0.5">{stats?.projects_pv ?? 0}</div>
                </div>
              </div>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/projekte?type=waermepumpe" data-testid="kpi-wp">
          <Card className="border-border hover:border-primary/60 transition-colors cursor-pointer">
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center"><Flame className="h-5 w-5 text-orange-500" /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Wärmepumpen-Projekte</div>
                  <div className="font-heading text-2xl font-bold mt-0.5">{stats?.projects_wp ?? 0}</div>
                </div>
              </div>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Sachbearbeiter-Auslastungs-Widget */}
      {workload && (workload.sachbearbeiter?.length > 0 || (workload.unassigned?.total ?? 0) > 0) && (
        <Card className="border-border" data-testid="workload-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-amber-500" /> Sachbearbeiter-Auslastung
            </CardTitle>
            <Link to="/anmeldungen" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="workload-link">
              Anmeldungen <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {(() => {
              const rows = workload.sachbearbeiter || [];
              const maxOpen = Math.max(1, ...rows.map((r) => r.open || 0), workload.unassigned?.open || 0);
              return (
                <ul className="divide-y divide-border">
                  {rows.map((sb) => (
                    <li key={sb.id} className="px-4 py-3" data-testid={`workload-row-${sb.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
                            <UserIcon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{sb.name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {sb.open} offen · {sb.done} erledigt · {sb.total} gesamt
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${sb.open > 0 ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"}`} data-testid={`workload-open-${sb.id}`}>
                            {sb.open} offen
                          </span>
                        </div>
                      </div>
                      {/* Progress-Bar: Verhältnis offen zum höchstbelasteten SB */}
                      <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div
                          className="h-full bg-amber-500 transition-all"
                          style={{ width: `${(sb.open / maxOpen) * 100}%` }}
                        />
                      </div>
                    </li>
                  ))}
                  {(workload.unassigned?.total ?? 0) > 0 && (
                    <li className="px-4 py-3 bg-destructive/5" data-testid="workload-row-unassigned">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
                            <UsersIcon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate text-destructive">Nicht zugewiesen</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {workload.unassigned.open} offen · {workload.unassigned.total} gesamt — bitte Bearbeiter zuweisen
                            </div>
                          </div>
                        </div>
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-destructive/10 text-destructive" data-testid="workload-unassigned-count">
                          {workload.unassigned.total}
                        </span>
                      </div>
                    </li>
                  )}
                  {rows.length === 0 && (workload.unassigned?.total ?? 0) === 0 && (
                    <li className="p-6 text-sm text-muted-foreground text-center">Noch keine Sachbearbeiter oder zugewiesenen Anmeldungen.</li>
                  )}
                </ul>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Nachrichten-Widget */}
      <Card className="border-border" data-testid="dashboard-messages-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" /> Nachrichten
            {unreadMsg > 0 && (
              <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-2 h-5 inline-flex items-center justify-center" data-testid="dashboard-msg-badge">
                {unreadMsg} neu
              </span>
            )}
          </CardTitle>
          <Link to="/nachrichten" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="dashboard-messages-link">
            Zum Chat <ArrowRight className="h-3 w-3" />
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {recentMsgs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Noch keine Nachrichten.</div>
          ) : (
            <ul className="divide-y divide-border">
              {recentMsgs.map((p) => (
                <li key={p.id}>
                  <Link to="/nachrichten" className="flex items-center justify-between px-4 py-2.5 hover:bg-secondary/60" data-testid={`dashboard-msg-${p.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <UserIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.name || p.email}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{p.last_message || "Neue Konversation"}</div>
                      </div>
                    </div>
                    {p.unread > 0 && (
                      <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 min-w-[1.25rem] h-5 inline-flex items-center justify-center shrink-0 ml-2">{p.unread}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Projekt-Kalender */}
      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" /> Projekt-Kalender
          </CardTitle>
          <Link to="/projekttermine" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="dashboard-kalender-link">
            Voller Kalender <ArrowRight className="h-3 w-3" />
          </Link>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6" data-testid="dashboard-kalender">
            <div>
              <Calendar
                mode="single"
                selected={calDate}
                onSelect={(d) => d && setCalDate(d)}
                components={{ DayContent }}
                className="rounded-md border border-border p-3"
              />
              <CategoryLegend className="mt-2 justify-center flex-wrap" />
            </div>
            <div>
              <div className="overline">
                {calDate.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
              </div>
              <div className="mt-2 space-y-2">
                {calDayItems.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2">Keine Termine an diesem Tag.</div>
                ) : calDayItems.map((t, i) => {
                  const cat = terminCategory(t);
                  return (
                    <Link
                      key={`d-${t.projectId}-${t.phase_key}-${i}`}
                      to="/projekttermine"
                      className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 hover:border-primary/60 transition-colors"
                      data-testid={`dashboard-cal-item-${t.projectId}`}
                    >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${CATEGORY_META[cat].color}`} />
                      {t.project_type === "pv" ? <Sun className="h-3.5 w-3.5 text-primary shrink-0" /> : <Flame className="h-3.5 w-3.5 text-purple-500 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{t.kunde_name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {t.phase_label}
                          {t.monteur_id && <> · <span className="text-primary inline-flex items-center gap-0.5"><UserIcon className="h-3 w-3" />{monteurName(t.monteur_id) || "Monteur"}</span></>}
                        </div>
                      </div>
                    </div>
                    </Link>
                  );
                })}
              </div>
              {upcoming.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Nächste Termine</div>
                  <ul className="space-y-1.5">
                    {upcoming.map((t, i) => (
                      <li key={`u-${t.projectId}-${t.phase_key}-${i}`} className="flex items-center justify-between text-xs gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {t.project_type === "pv" ? <Sun className="h-3 w-3 text-primary shrink-0" /> : <Flame className="h-3 w-3 text-purple-500 shrink-0" />}
                          <span className="truncate">
                            {t.kunde_name} · <span className="text-muted-foreground">{t.phase_label}</span>
                            {t.monteur_id && <> · <span className="text-primary inline-flex items-center gap-0.5"><UserIcon className="h-3 w-3" />{monteurName(t.monteur_id) || "Monteur"}</span></>}
                          </span>
                        </div>
                        <span className="font-mono text-[10px] text-muted-foreground ml-2 shrink-0">{t.termin}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" /> Anmeldungen nach Status
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <Link key={k} to={`/anmeldungen?status=${k}`} data-testid={`status-count-${k}`}>
              <div className="rounded-md border border-border p-3 hover:border-primary/60 transition-colors cursor-pointer">
                <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium status-${k}`}>{v}</div>
                <div className="font-heading text-2xl font-bold mt-2 tracking-tight">{stats?.by_status?.[k] ?? 0}</div>
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Neueste Anmeldungen</CardTitle>
            <Link to="/anmeldungen" className="text-xs text-primary hover:underline">Alle →</Link>
          </CardHeader>
          <CardContent className="p-0">
            {recent.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Noch keine Anmeldungen.</div>
            ) : (
              <ul className="divide-y divide-border">
                {recent.map((a) => (
                  <li key={a.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                    <div className="min-w-0"><div className="font-medium truncate">{a.kunde_name}</div><div className="text-xs text-muted-foreground truncate">{a.kunde_adresse}</div></div>
                    <div className={`px-2 py-0.5 rounded text-[10px] font-medium status-${a.status} shrink-0 ml-2`}>{STATUS_LABELS[a.status]}</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> Aktuelle Projekte</CardTitle>
            <Link to="/projekte" className="text-xs text-primary hover:underline">Alle →</Link>
          </CardHeader>
          <CardContent className="p-0">
            {projects.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Noch keine Projekte.</div>
            ) : (
              <ul className="divide-y divide-border">
                {projects.slice(0, 5).map((p) => (
                  <li key={p.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {p.project_type === "pv" ? <Sun className="h-4 w-4 text-primary shrink-0" /> : <Flame className="h-4 w-4 text-orange-500 shrink-0" />}
                      <div className="min-w-0"><div className="font-medium truncate">{p.kunde_name}</div><div className="text-xs text-muted-foreground truncate">{p.kunde_adresse}</div></div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
